/**
 * "Pair with a different account" (2026-09-30): the one control on the TV.
 *
 * A TV remembers its device token and resumes the account that paired it on
 * every load, so a TV paired to the wrong account (a test account, a
 * friend's phone) used to sit on "Waiting for your phone" with no way back
 * to a pairing code. The fix is a small, secondary action that forgets the
 * stored device, closes the session, and shows a fresh code. It is reachable
 * only while no phone is controlling the TV (the waiting and reconnecting
 * overlay), never over a live round, and asks before forgetting.
 *
 * Shared by the page (StatusHints.tsx, App.tsx) and the Apple TV app
 * (appletv/src/components/StatusHints.tsx, appletv/src/App.tsx) so the copy
 * and the rules are the same everywhere. Pure: no DOM, no storage.
 */

export const FORGET_TV_COPY = {
  action: 'Pair with a different account',
  title: 'Forget this TV?',
  body: 'It will show a new code to pair with your phone.',
  cancel: 'Cancel',
  confirm: 'Pair again',
} as const;

/** The URL parameter fallback: `tv.lagking.com/?forget` forgets and pairs. */
export const FORGET_TV_PARAM = 'forget';

/** Whether the query string asks the page to forget its stored device. */
export function forgetRequested(search: string): boolean {
  return new URLSearchParams(search).has(FORGET_TV_PARAM);
}

/**
 * The query string with the forget parameter removed, so a reload after
 * the new pairing does not forget the new device too. Returns `''` or a
 * string starting with `?`. Other parameters (`shell=webos`) are kept.
 */
export function searchWithoutForget(search: string): string {
  const params = new URLSearchParams(search);
  params.delete(FORGET_TV_PARAM);
  const rest = params.toString();
  return rest ? `?${rest}` : '';
}

/**
 * Whether the waiting/reconnecting overlay offers the action: only when the
 * TV has a stored device to forget (otherwise it is already pairing), and
 * only while the overlay is up, which means no phone is controlling it.
 */
export function showForgetAction(input: {
  hasStoredDevice: boolean;
  tvLinkFresh: boolean;
  reconnecting: boolean;
}): boolean {
  return input.hasStoredDevice && (!input.tvLinkFresh || input.reconnecting);
}
